import { AUTOPILOT_TO_ONE_TIME_QUERY_STRINGS } from 'constants/dbRecords';
import { IPageBase } from 'store/pages/types';

export const TOTAL_SLOTS = 6;
export const MAX_COMPLETE_DESKTOP = 3;
export const MAX_COMPLETE_MOBILE = 2;

// userData.issues uses "*" to mean "all issues".
const ALL_ISSUES_SENTINEL = '*';

const ISSUE_PRIORITY = ['close-the-gap', 'dt', 'gerrymandering', 'pd', 'ng', 'women'];

const issueRank = (page: IPageBase) => {
  const index = ISSUE_PRIORITY.indexOf(page.query_str);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

// One ranking across the whole pool, so personal and global issues sort by the
// same rule. Display name breaks ties; GET /pages has no ORDER BY of its own.
const byPriority = (a: IPageBase, b: IPageBase) =>
  issueRank(a) - issueRank(b) ||
  a.value.localeCompare(b.value) ||
  a.query_str.localeCompare(b.query_str);

// Cloned pages can share a display name (`hs` and `hs-full` are both "Flip the
// US Senate"), which would render two identical tiles. Keep the higher-priority one.
const dedupeByName = (pages: IPageBase[]) => {
  const seen = new Set<string>();
  return pages.filter(page => {
    if (seen.has(page.value)) return false;
    seen.add(page.value);
    return true;
  });
};

export type IssueSlot = {
  page: IPageBase;
  isComplete: boolean;
  visibleOn: { mobile: boolean; desktop: boolean };
};

export type FillOrder = {
  slots: IssueSlot[];
  // 0 means no overflow tile.
  overflow: { mobile: number; desktop: number };
  // Every completed issue in priority order, including those the card has no
  // room to show. The overflow tile opens a modal listing all of them.
  completed: IPageBase[];
};

const EMPTY: FillOrder = {
  slots: [],
  overflow: { mobile: 0, desktop: 0 },
  completed: [],
};

// The overflow tile takes a slot from the completed issues when it appears.
const visibleCompleteCount = (completeCount: number, max: number) =>
  completeCount > max ? max - 1 : Math.min(completeCount, max);

const overflowCount = (completeCount: number, max: number) =>
  completeCount > max ? completeCount - visibleCompleteCount(completeCount, max) : 0;

// `pages` arrives already screened: GET /pages filters on is_enabled and
// is_public_searchable, so nothing here re-checks either. Both breakpoints are
// returned in one list with per-breakpoint visibility, so the card renders the
// superset and CSS picks.
export const getFillOrder = (
  pages?: IPageBase[],
  personalIssueQueryStrs?: string[],
  completedQueryStrs?: string[]
): FillOrder => {
  if (!pages?.length) return EMPTY;

  const personal = personalIssueQueryStrs?.includes(ALL_ISSUES_SENTINEL)
    ? []
    : (personalIssueQueryStrs ?? []);
  // Donations to autopilot pages count toward the one-time page of the same issue.
  const completed = (completedQueryStrs ?? []).map(
    queryStr => AUTOPILOT_TO_ONE_TIME_QUERY_STRINGS[queryStr] ?? queryStr
  );

  const ranked = dedupeByName([...pages].sort(byPriority));

  const personalComplete: IPageBase[] = [];
  const globalComplete: IPageBase[] = [];
  const personalIncomplete: IPageBase[] = [];
  const globalIncomplete: IPageBase[] = [];

  ranked.forEach(page => {
    const isPersonal = personal.includes(page.query_str);
    const isComplete = completed.includes(page.query_str);
    if (isComplete) {
      (isPersonal ? personalComplete : globalComplete).push(page);
    } else {
      (isPersonal ? personalIncomplete : globalIncomplete).push(page);
    }
  });

  const complete = [...personalComplete, ...globalComplete];
  const incomplete = [...personalIncomplete, ...globalIncomplete];

  const shownDesktop = visibleCompleteCount(complete.length, MAX_COMPLETE_DESKTOP);
  const shownMobile = visibleCompleteCount(complete.length, MAX_COMPLETE_MOBILE);

  // Counts the overflow tile, unlike shown* above.
  const usedDesktop = Math.min(complete.length, MAX_COMPLETE_DESKTOP);
  const usedMobile = Math.min(complete.length, MAX_COMPLETE_MOBILE);

  const incompleteSlotsDesktop = TOTAL_SLOTS - usedDesktop;
  const incompleteSlotsMobile = TOTAL_SLOTS - usedMobile;

  // Mobile has room for at least as many incomplete issues as desktop, so render
  // to the mobile count and hide the tail on desktop.
  const slots: IssueSlot[] = [
    ...complete.slice(0, shownDesktop).map((page, index) => ({
      page,
      isComplete: true,
      visibleOn: { mobile: index < shownMobile, desktop: true },
    })),
    ...incomplete.slice(0, incompleteSlotsMobile).map((page, index) => ({
      page,
      isComplete: false,
      visibleOn: { mobile: true, desktop: index < incompleteSlotsDesktop },
    })),
  ];

  return {
    slots,
    overflow: {
      mobile: overflowCount(complete.length, MAX_COMPLETE_MOBILE),
      desktop: overflowCount(complete.length, MAX_COMPLETE_DESKTOP),
    },
    completed: complete,
  };
};
